import { useEffect, useState, useRef, useCallback } from 'react';
import { DropzoneRef } from 'react-dropzone';
import { useCreateArtifact, useGetArtifacts } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Box,
  Button,
  CircularProgress,
  ControllerGridQuickFilterToolbar,
  Modal,
} from '@bymason-private/legacy-pattern-components';
import {
  useToast,
  GridGroupingOption,
  persistGridGrouping,
  retreiveGridGrouping,
  GroupingToggleButton,
} from '@bymason-private/controller-shared-widgets';
import { getDataSize } from '@bymason/utils';

import Layout from '../../common/layout';
import { DropzoneContainer, DropzoneDropEvent } from '../../common/dropzone-container';
import ArtifactsList from './artifacts-list';
import NoArtifacts from './no-artifacts';

export default function ArtifactsView() {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getArtifacts, artifacts, artifactsError, artifactsBusy] = useGetArtifacts(authToken);
  const [uploadArtifact, uploadResult, uploadError, uploadBusy, , , clearUpload] = useCreateArtifact(
    authToken
  );
  const [otherError, setOtherError] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState<number>();
  const [artifactsView, setArtifactsView] = useState<GridGroupingOption>(
    retreiveGridGrouping('artifacts') || 'grouped'
  );

  const dropzoneRef = useRef<DropzoneRef>();

  const resetUpload = useCallback(() => {
    clearUpload();
    setOtherError('');
    setFileName('');
    setFileSize(undefined);
    setToastMessage('');
  }, [clearUpload, setToastMessage]);

  const handleUploadFileButtonClick = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const handleDrop: DropzoneDropEvent = useCallback(
    (dropResult) => {
      const { files, rejectionCount } = dropResult;
      resetUpload();

      if (files.length > 1 || rejectionCount > 1) {
        setOtherError('Please upload one file at a time');
      } else if (files.length === 1) {
        const file = files[0];

        const selectionType = getUploadType(file.name);
        if (selectionType) {
          setFileName(file.name);
          setFileSize(file.size);
          uploadArtifact({
            type: selectionType,
            file,
            fileName: file.name,
          });
        } else {
          setOtherError('Artifact type cannot be determined from file extension');
        }
      } else {
        rejectionCount && setOtherError(`File was not uploaded because the file type was incompatible`);
      }
    },
    [uploadArtifact, resetUpload]
  );

  const handleChangeArtifactView = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: unknown) => {
    if (value) {
      const newValue = value as GridGroupingOption;
      persistGridGrouping('artifacts', newValue);
      setArtifactsView(newValue);
    }
  };

  useEffect(() => {
    if (customerId) {
      getArtifacts({ customerId });
    }
  }, [customerId, getArtifacts]);

  useEffect(() => {
    // Order matters. Show sync (processing) errors before async (api) errors
    const message = otherError || artifactsError || uploadError;
    if (message) {
      setToastMessage(message);
      setToastSeverity('error');
    }
  }, [artifactsError, uploadError, otherError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (customerId && uploadResult && !artifactsError) {
      getArtifacts({ customerId });
      setToastMessage(`${uploadResult.name} version ${uploadResult.version} has been successfully created`);
      setToastSeverity('success');
    }
  }, [artifactsError, customerId, getArtifacts, setToastMessage, setToastSeverity, uploadResult]);

  return (
    <Layout
      busy={artifactsBusy}
      headerText='Artifacts'
      headerRightAdornment={
        <Box display='flex' columnGap={1}>
          <Button variant='outlined' onClick={handleUploadFileButtonClick}>
            Upload new artifact
          </Button>
          <GroupingToggleButton grouping={artifactsView} onGroupingChange={handleChangeArtifactView} />
        </Box>
      }
      stickyHeader
    >
      <DropzoneContainer dropzoneRef={dropzoneRef} onFileDrop={handleDrop} accept={['.apk', '.png', '.zip']}>
        <ArtifactsList
          artifacts={artifacts}
          isBusy={artifactsBusy}
          isGrouped={artifactsView === 'grouped'}
          linkConfig
          noDataMessage={<NoArtifacts />}
          components={{
            Toolbar: ControllerGridQuickFilterToolbar,
          }}
          componentsProps={{
            toolbar: { density: 'compact' },
          }}
        />
      </DropzoneContainer>
      <Modal
        disablePortal
        open={!!fileName && !uploadError && uploadBusy}
        sx={{ position: 'absolute' }}
        BackdropProps={{ sx: { position: 'absolute' } }}
      >
        <Box
          width='100%'
          height='100%'
          display='flex'
          justifyContent='center'
          alignItems='center'
          color='white'
        >
          <strong>{`Uploading ${fileName} ${fileSize ? ` (${getDataSize(fileSize)})` : ''}`}</strong>
          <CircularProgress size='1em' color='inherit' sx={{ marginLeft: 1 }} />
        </Box>
      </Modal>
    </Layout>
  );
}

// helpers

type UploadType = 'apk' | 'splash' | 'bootanimation';

const getUploadType = (fileName: string): UploadType | undefined => {
  const extension = fileName.slice(fileName.lastIndexOf('.'));
  switch (extension.toLocaleLowerCase()) {
    case '.apk':
      return 'apk';
    case '.png':
      return 'splash';
    case '.zip':
      return 'bootanimation';
  }
};
